/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { InjectionToken } from '@angular/core';
import { clamp, noop } from 'lodash';

import { Observable } from 'rxjs';

export const VIRTUAL_CONTAINER_PROVIDER = new InjectionToken<VirtualContainerProvider>('VirtualContainerProvider');

export type VirtualPosition = {
  left: number;
  top: number;
};

export type VirtualBox = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};
export type VirtualRange = {
  start: number;
  end: number;
};
export type VirtualContainer = VirtualBox;
export type VirtualViewport = VirtualBox;

export const EMPTY_BOX: VirtualBox = { left: 0, top: 0, right: 0, bottom: 0 };

/**
 * Container for virtual items.
 */
export interface VirtualContainerProvider {
  /** This stream emits new value when visible part of container is changed */
  container: Observable<VirtualContainer>;
  /** Returns current visible part of container */
  containerSnapshot: VirtualContainer;

  /** Calculates offset of given element in this container */
  calculateOffset(element: HTMLElement): VirtualPosition;

  /** Sets dimensions of container */
  setDimensions(width?: number, height?: number): void;
}

/**
 * Returns true if given <code>box</code> fits into the <code>viewport</code>, otherwise false
 */
export const isFitInVirtualViewport = (viewport: VirtualViewport, box: VirtualContainer) =>
  !(box.right < viewport.left || box.left > viewport.right || box.bottom < viewport.top || box.top > viewport.bottom);

/**
 * Virtual items are positioned absolutely.
 * {@link VirtualItemSizeStrategy} defines logic
 * <ul>
 *   <li>to calculate visible range of items in {@link VirtualListComponent}</li>
 *   <li>to calculate position of each element in {@link VirtualListComponent}</li>
 *   <li>to calculate size of {@link VirtualListComponent}</li>
 * </ul>
 *
 * In the simplest case {@link VirtualItemSizeStrategy} can take constant size of each item.
 * In more complex case {@link VirtualItemSizeStrategy} allows to implement changing of item size
 * and notify {@link VirtualListComponent} about this change
 */
export interface VirtualItemSizeStrategy<T> {
  calculateFullSize(items: T[]): number;

  calculateVisibleRange(rangeInPx: VirtualRange, items: T[]): VirtualRange;

  calculatePosition(item: T, index: number): number;

  registerOnSizeChange(fn: () => void): void;
}

export interface VirtualItemDisposeStrategy<T> {
  (item: T): boolean;
}

/**
 * The simplest {@link VirtualItemSizeStrategy} for items having constant size
 * @param itemSize
 */
export const constantVirtualItemSizeStrategy = (itemSize: number): VirtualItemSizeStrategy<any> => ({
  calculateFullSize: (items) => items.length * itemSize,
  calculateVisibleRange: ({ start, end }, items) => ({
    start: clamp(Math.floor(start / itemSize), 0, items.length),
    end: clamp(Math.ceil(end / itemSize) + 1, 0, items.length),
  }),
  calculatePosition: (_, position) => position * itemSize,
  registerOnSizeChange: noop,
});

export const isRangeContains = (range: VirtualRange, anotherRange: VirtualRange) =>
  range.start <= anotherRange.start && range.end >= anotherRange.end;

export const expandRange = (range: VirtualRange, maxLength: number, expandSize: number) => ({
  start: Math.max(0, range.start - expandSize),
  end: Math.min(maxLength, range.end + expandSize),
});
